import {
  type AreaData,
  type AreaId,
  type AreaListInput,
  AreaListInputSchema,
  type AreaListSuccess,
  isDefined,
  isNotDefined,
  MINUTE,
} from '@terros/common'
import { type InfiniteData, type QueryClient, type QueryKey } from '@tanstack/react-query'
import { areaCountKey, areaGetKey, areaListInfiniteKey } from '../keys'
import { applyAreaToPages, areaCountDelta } from './listPages'
import { type AreaListMatch, matchAreaListInput } from './listMatch'

/** v2 lists read Elastic, which lags writes, so refetching them right away drops the change. Same delay as assign. */
export const AREA_LIST_REFRESH_DELAY_MS = MINUTE

/** The area before a write: 'new' when it did not exist, undefined when no cache had it. */
export type AreaBefore = AreaData | 'new' | undefined

export type AreaChange = {
  areaId: AreaId
  before: AreaBefore
  /** The area after the write, or undefined when it was hard deleted. */
  after: AreaData | undefined
}

/** Patches every cached v2 area list and count for one write, respecting each query's filters. */
export function applyAreaChangeToV2Caches(queryClient: QueryClient, { areaId, before, after }: AreaChange): void {
  for (const queryKey of cachedKeys(queryClient, areaListInfiniteKey())) {
    const input = inputOf(queryKey)
    if (isNotDefined(input)) continue
    const match = matchAfter(after, input)
    queryClient.setQueryData<InfiniteData<AreaListSuccess>>(queryKey, (data) =>
      isDefined(data) ? applyAreaToPages({ data, areaId, after, match }) : data
    )
  }
  for (const queryKey of cachedKeys(queryClient, areaCountKey())) {
    const input = inputOf(queryKey)
    if (isNotDefined(input)) continue
    const delta = areaCountDelta(matchBefore(before, input), matchAfter(after, input))
    if (delta === 0) continue
    queryClient.setQueryData<number>(queryKey, (count) => (isDefined(count) ? Math.max(count + delta, 0) : count))
  }
}

/** The cached copy of an area from /area/get or a v2 list, if any query has it. */
export function findCachedArea(queryClient: QueryClient, areaId: AreaId): AreaData | undefined {
  return queryClient.getQueryData<AreaData>(areaGetKey(areaId)) ?? findListedArea(queryClient, areaId)
}

/** The copy of an area in any cached v2 list, so a detail view can show it while /area/get loads. */
export function findListedArea(queryClient: QueryClient, areaId: AreaId): AreaData | undefined {
  for (const [, data] of queryClient.getQueriesData<InfiniteData<AreaListSuccess>>({
    queryKey: areaListInfiniteKey(),
  })) {
    const found = data?.pages.flatMap((page) => page.areas).find((a) => a.areaId === areaId)
    if (isDefined(found)) return found
  }
  return undefined
}

/** Cancels the pending refresh for each query client. */
const pendingRefreshes = new WeakMap<QueryClient, VoidFunction>()

/** Refetches v2 lists and counts once Elastic has likely indexed the write. Repeated writes push the refetch back. */
export function scheduleAreaListRefresh(queryClient: QueryClient): void {
  pendingRefreshes.get(queryClient)?.()
  const timer = setTimeout(() => {
    pendingRefreshes.delete(queryClient)
    queryClient.invalidateQueries({ queryKey: areaListInfiniteKey() })
    queryClient.invalidateQueries({ queryKey: areaCountKey() })
  }, AREA_LIST_REFRESH_DELAY_MS)
  pendingRefreshes.set(queryClient, () => clearTimeout(timer))
}

function cachedKeys(queryClient: QueryClient, prefix: QueryKey): QueryKey[] {
  return queryClient
    .getQueryCache()
    .findAll({ queryKey: prefix })
    .map((query) => query.queryKey)
}

/** The list input a cached area list or count query was made with, or undefined when the key holds no valid input. */
function inputOf(queryKey: QueryKey): AreaListInput | undefined {
  const parsed = AreaListInputSchema.safeParse(queryKey[1] ?? {})
  return parsed.success ? parsed.data : undefined
}

function matchBefore(before: AreaBefore, input: AreaListInput): AreaListMatch {
  if (before === 'new') return false
  if (isDefined(before)) return matchAreaListInput(before, input)
  return undefined
}

function matchAfter(after: AreaData | undefined, input: AreaListInput): AreaListMatch {
  if (isDefined(after)) return matchAreaListInput(after, input)
  return false
}
